// A plan's body (today's components/EditableMarkdown.tsx and
// markdown/MarkdownEditor.tsx, ported): drawn as its page, or edited as its
// source beside a preview, saved with the base it started from so that the
// server merges a concurrent edit. A save the server cannot merge is refused
// (409 `text_conflict`): the editor stays open with the draft, and the
// refused change, with both texts, waits under Not synced, as an issue's
// parts do. An HTML plan is edited as its HTML; its preview goes through the
// plan's two gates (sanitize.ts, html.tsx), never into the page as a string.
// Today's editor is CodeMirror's; these pages have no CodeMirror yet, so the
// source is today's plain fallback.
import { startTransition, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'
import type { PlanDetail } from '../api/types'
import { useUnsavedChanges } from '../components/unsaved'
import { renderMarkdown } from '../markdown/render'
import { useStore } from '../store/context'
import { useWorkspaceDraft } from '../workspace/drafts'
import { formatOf } from './format'
import { contentNodes, PlanHtml } from './html'
import { sanitizePlanFragment } from './sanitize'
import { pageWords as w } from './words'

interface Edit {
  base: string
  draft: string
}

const isEdit = (value: unknown): value is Edit | null =>
  value === null || (typeof value === 'object' && value !== null && 'base' in value && typeof value.base === 'string' && 'draft' in value && typeof value.draft === 'string')

export function PlanBody({ plan, view, onEditingChange }: {
  plan: PlanDetail
  /** What the page shows of the body: an HTML plan's page, or a Markdown plan's text. */
  view: ReactNode
  onEditingChange?: (editing: boolean) => void
}) {
  const store = useStore()
  const [edit, setEdit] = useWorkspaceDraft<Edit | null>(`plan.${plan.id}.body`, null, isEdit, (value) => value !== null && value.draft !== value.base)
  const [saving, setSaving] = useState(false)
  const dirty = edit !== null && edit.draft !== edit.base
  const editing = edit !== null
  useUnsavedChanges(dirty)

  useEffect(() => {
    onEditingChange?.(editing)
  }, [editing, onEditingChange])

  const submit = async () => {
    if (!edit || saving) return
    if (!dirty) {
      setEdit(null)
      return
    }
    setSaving(true)
    // From the body the edit started from. A refused save keeps the editor open with the draft; the store has said why.
    const saved = await store.setPlanBody(plan.id, edit.draft, edit.base)
    setSaving(false)
    if (saved) setEdit(null)
  }

  const cancel = () => {
    if (saving) return
    if (dirty && !window.confirm('Discard your unsaved changes?')) return
    setEdit(null)
  }

  if (edit) {
    return (
      <div className="markdown-editing">
        <PlanEditor value={edit.draft} onChange={(draft) => setEdit((e) => (e ? { ...e, draft } : e))} onSave={() => void submit()} onCancel={cancel} />
        <div className="form-actions">
          <span className="muted form-hint form-hint-keys">{w.keys}</span>
          <button type="button" className="button" onClick={cancel} disabled={saving}>
            {w.cancel}
          </button>
          <button type="button" className="button button-primary" onClick={() => void submit()} disabled={saving}>
            {saving ? w.saving : w.save}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="markdown-display">
      <div className="markdown-display-actions">
        <button type="button" className="button button-quiet button-small" onClick={() => setEdit({ base: plan.body, draft: plan.body })}>
          {w.editPlan}
        </button>
      </div>
      {!plan.body.trim() ? <p className="muted">{w.empty}</p> : view}
    </div>
  )
}

// ---------------------------------------------------------------- the editor

const PREVIEW_DELAY_MS = 150
const PREVIEW_DELAY_LARGE_MS = 300
const PREVIEW_LARGE_CHARS = 20_000
const PREVIEW_MAX_WAIT_MS = 1_000

/** The value the preview shows: trails typing, but never by more than a second. */
function usePreviewSource(value: string): string {
  const [preview, setPreview] = useState(value)
  const pendingSince = useRef<number | null>(null)
  useEffect(() => {
    if (value === preview) return
    const now = performance.now()
    pendingSince.current ??= now
    const delay = value.length > PREVIEW_LARGE_CHARS ? PREVIEW_DELAY_LARGE_MS : PREVIEW_DELAY_MS
    const wait = Math.max(0, Math.min(delay, pendingSince.current + PREVIEW_MAX_WAIT_MS - now))
    const timer = setTimeout(() => {
      pendingSince.current = null
      startTransition(() => setPreview(value))
    }, wait)
    return () => clearTimeout(timer)
  }, [value, preview])
  return preview
}

/** The source beside its preview: ⌘S / Ctrl+S saves, Esc cancels. Links in the preview open nowhere: the text is not saved. */
export function PlanEditor({ value, onChange, onSave, onCancel }: { value: string; onChange: (value: string) => void; onSave: () => void; onCancel: () => void }) {
  const preview = usePreviewSource(value)
  const html = formatOf(value) === 'html'
  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || event.nativeEvent.isComposing) return
    const command = event.metaKey || event.ctrlKey
    if (command && !event.altKey && !event.shiftKey && (event.key.toLowerCase() === 's' || event.code === 'KeyS')) {
      event.preventDefault()
      onSave()
    } else if (event.key === 'Escape' && !command && !event.altKey && !event.shiftKey) {
      event.preventDefault()
      onCancel()
    }
  }
  return (
    <div className="markdown-body md-editor" onKeyDown={onKeyDown}>
      <div className="md-editor-panes">
        <div className="md-editor-source">
          <textarea className="md-editor-plain" aria-label={html ? w.htmlSource : w.markdownSource} spellCheck autoFocus value={value} onChange={(event) => onChange(event.target.value)} />
        </div>
        <section
          className="md-editor-preview"
          aria-label={w.preview}
          onClickCapture={(event) => {
            // The preview's links lead nowhere while the text is unsaved.
            if ((event.target as Element).closest?.('a[href]')) event.preventDefault()
          }}
        >
          <PlanSource source={preview} />
        </section>
      </div>
    </div>
  )
}

/** A body as it is written, through the plan's gates: HTML as its sanitised elements, Markdown rendered; keys plain. */
export function PlanSource({ source, className }: { source: string; className?: string }) {
  const html = formatOf(source) === 'html'
  const content = useMemo(() => [...sanitizePlanFragment(html ? source : renderMarkdown(source)).childNodes], [source, html])
  const nodes = useMemo(() => contentNodes(content, [], 'plain'), [content])
  return <div className={className ? `markdown-body ${className}` : 'markdown-body'}>{nodes}</div>
}

/** A Markdown plan's text, rendered, through the plan's gates: its issue keys linked, with their live states. */
export function MarkdownPlan({ source }: { source: string }) {
  const content = useMemo(() => [...sanitizePlanFragment(renderMarkdown(source)).childNodes], [source])
  return <PlanHtml as="div" className="markdown-body md-document" content={content} mode="chip" />
}
